import {
  useCustomMutation,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Button, Card, Divider, Flex, Tag, Typography, theme } from 'antd';
import {
  ArrowRightOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type {
  PublicationImportNewOut,
  PublisherDetailOut,
  PublisherImportOut,
} from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';
import { orDash } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';

const { Text } = Typography;

interface PublisherSourceCardProps {
  record: PublisherDetailOut;
  onSynced: () => void;
}

export const PublisherSourceCard = ({
  record,
  onSynced,
}: PublisherSourceCardProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { open } = useNotification();

  const isImprint = record.parent_id != null;
  const isSynced = !record.source_has_update;

  const { mutate: synchronize, mutation: syncMutation } =
    useCustomMutation<PublisherImportOut>();

  const handleSynchronize = () => {
    if (record.id == null) return;
    synchronize(
      {
        url: `user_catalog/publishers/${record.id}/import`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: ({ data: summary }) => {
          open?.({
            type: 'success',
            description: t('notifications.success'),
            message: t('user_catalog.publishers.synchronize.summary', {
              updated: summary.updated,
              skipped: summary.skipped,
              errors: summary.errors.length,
            }),
          });
          onSynced();
        },
      }
    );
  };

  const { mutate: importPublications, mutation: importMutation } =
    useCustomMutation<PublicationImportNewOut>();

  const handleImportPublications = () => {
    if (record.source_id == null) return;
    importPublications(
      {
        url: `user_catalog/publications/import_new?publisher_id=${record.source_id}`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: ({ data: summary }) => {
          open?.({
            type: 'success',
            description: t('notifications.success'),
            message: t('user_catalog.publishers.import_publications.summary', {
              scanned: summary.scanned,
              enqueued: summary.enqueued,
              already_existing: summary.already_existing,
            }),
          });
        },
      }
    );
  };

  if (record.source_id == null) return null;

  return (
    <Card
      title={t('base.sections.source_info')}
      extra={
        !isImprint && (
          <Tag color={isSynced ? 'success' : 'warning'}>
            {isSynced
              ? t('user_catalog.publishers.synchronize.synced')
              : t('user_catalog.publishers.synchronize.update_available')}
          </Tag>
        )
      }
    >
      <Flex vertical gap={token.marginSM}>
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {t('user_catalog.publishers.show.source_record')}
        </Text>

        <div
          style={{
            background: token.colorFillQuaternary,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: token.paddingSM,
          }}
        >
          <Flex gap={token.marginSM} align="center" justify="space-between">
            <Flex vertical gap={token.marginXXS}>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('content_catalog.content_catalog')}
              </Text>
              <Flex gap={token.marginXS} align="baseline" wrap>
                {isImprint ? (
                  <Text strong>{record.name}</Text>
                ) : (
                  <ShowLink
                    resource="content_catalog.publishers"
                    id={record.source_id}
                  >
                    <Text strong style={{ color: 'inherit' }}>
                      {record.name}
                    </Text>
                  </ShowLink>
                )}
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  ID {record.source_id}
                </Text>
              </Flex>
            </Flex>
            {!isImprint && (
              <div style={{ whiteSpace: 'nowrap' }}>
                <ShowLink
                  resource="content_catalog.publishers"
                  id={record.source_id}
                >
                  {t('user_catalog.publishers.show.open')}{' '}
                  <ArrowRightOutlined />
                </ShowLink>
              </div>
            )}
          </Flex>
        </div>

        <Text type="secondary">
          {t('user_catalog.publishers.fields.source_updated_at')}:{' '}
          {orDash(formatDateTime(record.source_updated_at))}
        </Text>

        {isImprint ? (
          <Text type="secondary" italic>
            {t('user_catalog.publishers.synchronize.imprint_note')}
          </Text>
        ) : (
          <>
            <Divider style={{ margin: 0 }} />
            <Flex vertical gap={token.marginXS}>
              <Button
                block
                icon={<ReloadOutlined />}
                loading={syncMutation.isPending}
                onClick={handleSynchronize}
              >
                {t('user_catalog.publishers.synchronize.button')}
              </Button>
              {/* Bulk field resync (NOV-903) joins this row when its
                  modal lands; `1 1 auto` so labels never clip. */}
              <Flex gap={token.marginXS} wrap>
                <Button
                  icon={<DownloadOutlined />}
                  loading={importMutation.isPending}
                  onClick={handleImportPublications}
                  style={{ flex: '1 1 auto' }}
                >
                  {t('user_catalog.publishers.import_publications.button')}
                </Button>
              </Flex>
            </Flex>
          </>
        )}
      </Flex>
    </Card>
  );
};
